import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FamilyGeneration } from '../../../models/familyGeneration';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-family-generation-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './family-generation-list.component.html',
  styleUrl: './family-generation-list.component.scss'
})
export class FamilyGenerationListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  familyGenerations = signal<FamilyGeneration[]>([]);
  rightItem: FamilyGeneration | undefined = undefined;
  matchedItems: FamilyGeneration[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadFamilyGenerations();
  }

  loadFamilyGenerations(): void {
    this.spinnerService.show();
    this.lutService.getFamilyGenerations().subscribe({
      next: (data) => {
        const sortedData = this.sortFamilyGenerations(data);
        this.familyGenerations.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading family generations:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortFamilyGenerations(familyGenerations: FamilyGeneration[]): FamilyGeneration[] {
    return [...familyGenerations].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateFamilyGeneration(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(familyGeneration: FamilyGeneration): void {
  }

  onEditFamilyGeneration(familyGeneration: FamilyGeneration): void {
  }

  onDeleteFamilyGeneration(familyGeneration: FamilyGeneration): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(familyGeneration.name)
      .subscribe(result => {
        if (result) {
          const originalFamilyGenerations = this.familyGenerations();

          this.familyGenerations.update(items => items.filter(fg => fg.id !== familyGeneration.id));

          this.spinnerService.show();
          this.lutService.deleteFamilyGeneration(familyGeneration.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Family generation deleted successfully');
            },
            error: (error) => {
              console.error('Error deleting family generation:', error);
              this.spinnerService.hide();
              this.familyGenerations.set(originalFamilyGenerations);
              this.toast.error(`<strong>Failed to delete Family Generation:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: FamilyGeneration, newName: string} | {item: FamilyGeneration, newValue: string, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: FamilyGeneration;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.familyGenerations.update(items => {
      const updated = items.map(fg => fg.id === event.item.id ? updatedItem : fg);
      return this.sortFamilyGenerations(updated);
    });

    this.spinnerService.show();
    this.lutService.updateFamilyGeneration(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Family generation updated successfully');
        this.familyGenerations.update(items => {
          const updated = items.map(fg => fg.id === event.item.id ? response : fg);
          return this.sortFamilyGenerations(updated);
        });
      },
      error: (error) => {
        console.error('Error updating family generation:', error);
        this.spinnerService.hide();
        this.familyGenerations.update(items =>
          items.map(fg => fg.id === event.item.id ? originalItem : fg)
        );
        this.toast.error(`<strong>Failed to update Family Generation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newFamilyGeneration: FamilyGeneration = { id: tempId, name: event['name'], systemReserved: false };

    this.familyGenerations.update(items => this.sortFamilyGenerations([...items, newFamilyGeneration]));

    this.spinnerService.show();
    this.lutService.createFamilyGeneration({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Family generation created successfully');
        this.familyGenerations.update(items => {
          const updated = items.map(fg => fg.id === tempId ? response : fg);
          return this.sortFamilyGenerations(updated);
        });
      },
      error: (error) => {
        console.error('Error creating family generation:', error);
        this.spinnerService.hide();
        this.familyGenerations.update(items =>
          items.filter(fg => fg.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Family Generation:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
